import React from 'react';
import { formatEnglishMonthDay } from '@/common/utils/formatEnglishDateTime';
import {
  Text, Box, HStack, Icon, Pressable, Badge, BadgeText,
} from '@gluestack-ui/themed';
import {
  Check, Calendar, Lock, Trophy,
} from 'lucide-react-native';
import { useEventTaskSlots } from '../hooks/useEventTaskSlots';
import { useTenantEventKinds } from '../../../common/context/TenantSettingsStore';
import { taskRequiresEvaluation } from '../../../common/utils/taskEvaluationUtils';
import { isEventDeliveredTask } from '../../../common/utils/tasks/isEventDeliveredTask';

const getTaskCompletionStatus = (task) => task.submission?.state === 'marked_as_done';

// Icon displayed at the end (completion status)
const TaskEndIcon = ({ task }) => {
  const isCompleted = getTaskCompletionStatus(task);

  if (!isCompleted) return null;

  return <Icon as={Check} size="xs" color="$green600" testID="completion-check" />;
};

// Icon displayed inline with the title (contextual display)
const TaskInlineIcon = ({ task, currentBooking }) => {
  const { eventKinds } = useTenantEventKinds();
  const isEventTask = isEventDeliveredTask(task);
  const evaluationRequired = taskRequiresEvaluation(task, eventKinds);
  // Tasks with full_evaluation grading system (transfer tasks, oral exams) contribute to the module grade.
  const isGraded = evaluationRequired && task.gradingSystem === 'full_evaluation';
  const isLocked = task.eventConfig?.locked;
  const isCompleted = getTaskCompletionStatus(task);

  // Priority 1: Lock for locked events
  if (isEventTask && isLocked) {
    return <Icon as={Lock} size="2xs" color="$textSubtle" testID="event-locked" />;
  }

  // Priority 2: Date pill for booked events (pending leftovers display like confirmed)
  if (isEventTask && currentBooking) {
    if (currentBooking.status === 'pending' || currentBooking.status === 'confirmed' || currentBooking.status === 'completed') {
      const dateStr = formatEnglishMonthDay(currentBooking.startTime);

      return (
        <Badge size="sm" bg="$backgroundLight200" testID="event-date-pill">
          <BadgeText size="xs" color="$textSubtle">
            {dateStr}
          </BadgeText>
        </Badge>
      );
    }

    return <Icon as={Calendar} size="2xs" color="$textSubtle" testID="event-calendar" />;
  }

  // Priority 3: Calendar for open events
  if (isEventTask) {
    return <Icon as={Calendar} size="2xs" color="$textSubtle" testID="event-calendar" />;
  }

  // Priority 4: Trophy for tasks that contribute to the module grade
  if (isGraded) {
    return (
      <Icon
        as={Trophy}
        size="2xs"
        color={isCompleted ? '$textDisabled' : '$textSubtle'}
        testID="transfer-task"
      />
    );
  }

  return null;
};

/**
 * TaskItemDesktop
 * ---------------
 * Desktop-optimized task item component designed for sidebar usage.
 * Features compact sizing, selection states, and precise click targets.
 */
const TaskItemDesktop = ({
  task, handleSelectTask, currentTask, module, selectedModule,
}) => {
  const courseId = selectedModule?.courseId || module?.courses?.[0]?.id;
  const moduleId = module?.id;
  const moduleVersion = module?.version;
  const isSelected = task.id === currentTask?.id;
  const { currentBooking } = useEventTaskSlots(courseId, moduleId, moduleVersion, task.id, {
    enabled: isEventDeliveredTask(task) && !task.eventConfig?.locked && isSelected,
    pollSlots: false,
    pollBookings: false,
    task,
  });

  const isCompleted = getTaskCompletionStatus(task);

  const handlePress = () => {
    handleSelectTask(task);
  };

  return (
    <Pressable
      onPress={handlePress}
      testID={`task-item-${task.id}`}
      mx={isSelected ? '-$2' : '0'}
      px={isSelected ? '$2' : '0'}
      bg={isSelected ? '$backgroundLight50' : 'transparent'}
      borderColor={isSelected ? '$backgroundLight200' : 'transparent'}
      borderWidth={1}
      borderRadius="$md"
    >
      <HStack alignItems="center" py="$1.5" justifyContent="space-between">
        <HStack alignItems="center" flex={1}>
          <Text
            size="xs"
            numberOfLines={1}
            color={isCompleted ? '$textSubtle' : '$textHeading'}
            opacity={isCompleted ? 0.6 : 1}
            mr="$2"
          >
            {task.title}
          </Text>

          <Box mb={2}>
            <TaskInlineIcon task={task} currentBooking={currentBooking} />
          </Box>
        </HStack>

        <HStack alignItems="center" space="sm">
          <Box w={16} alignItems="center">
            <TaskEndIcon task={task} />
          </Box>
        </HStack>
      </HStack>
    </Pressable>
  );
};

export default TaskItemDesktop;
